/**
 * ZENGİN DASHBOARD — Restoran sahibi tek ekranda her şeyi görür.
 *
 * 4 KPI Card + 6 Grafik Widget + 4 Küçük Widget + Real-time refresh.
 * Rol bazlı görünüm: OWNER/MANAGER/ACCOUNTANT farklı widget'lar.
 */
import {
  Badge, Card, Grid, Group, Loader, Paper, ScrollArea,
  SimpleGrid, Stack, Table, Text, Title,
} from "@mantine/core";
import { AreaChart, BarChart, DonutChart, LineChart, PieChart } from "@mantine/charts";
import {
  IconAlertTriangle, IconChefHat, IconClock, IconCoins,
  IconCurrencyLira, IconReceipt, IconShoppingCart, IconTrendingDown,
  IconTrendingUp, IconUsers,
} from "@tabler/icons-react";
import { useAuth } from "../../../shared/auth";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDailyCashier, useProductSales, useStockAlerts,
  useTableStatusOverview, useDashboardSummary, useMarginAlerts,
} from "../../../shared/api";
import { useWebSocket } from "../../../shared/ws";
import { KPICard, MoneyDisplay, PageHeader, formatTRY } from "../../../shared/ui";

export function Dashboard() {
  const { user } = useAuth();
  const { data: daily, isLoading: dailyLoading } = useDailyCashier();
  const { data: sales } = useProductSales();
  const { data: alerts } = useStockAlerts();
  const { data: tableOverview } = useTableStatusOverview();
  const { data: summary } = useDashboardSummary();
  const { data: marginAlerts } = useMarginAlerts(30);

  // WS push — order/payment/stock event'lerinde dashboard refetch
  const qc = useQueryClient();
  const { lastMessage, status: wsStatus } = useWebSocket<any>("/ws/kds/");
  useEffect(() => {
    if (!lastMessage) return;
    qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
    qc.invalidateQueries({ queryKey: ["daily-cashier"] });
    qc.invalidateQueries({ queryKey: ["table-overview"] });
  }, [lastMessage, qc]);

  const last7Data = summary?.last_7_days?.map((d: any) => ({
    day: new Date(d.date).toLocaleDateString("tr-TR", { weekday: "short" }),
    Ciro: d.revenue_minor / 100,
  })) || [];

  // "vs dün" deltaları — son 7 günden bugün vs dün karşılaştırması.
  // Backend last_7_days: index 6 = bugün, index 5 = dün.
  const last7 = summary?.last_7_days ?? [];
  const todayRow = last7.length >= 1 ? last7[last7.length - 1] : null;
  const yesterdayRow = last7.length >= 2 ? last7[last7.length - 2] : null;

  const pctDelta = (today: number, yesterday: number): number | null => {
    if (yesterday === 0) return today === 0 ? 0 : null; // bölünemez → trend gizle
    return ((today - yesterday) / yesterday) * 100;
  };

  const todayRevenue = daily?.total_revenue_minor ?? todayRow?.revenue_minor ?? 0;
  const yesterdayRevenue = yesterdayRow?.revenue_minor ?? 0;
  const revenueDelta = pctDelta(todayRevenue, yesterdayRevenue);

  const todayOrderCount = (daily?.total_orders ?? 0) - (daily?.cancelled_count ?? 0);
  const yesterdayOrderCount = (yesterdayRow?.orders ?? 0) - (yesterdayRow?.cancelled ?? 0);
  const ordersDelta = pctDelta(todayOrderCount, yesterdayOrderCount);

  const todayNetProfit = daily?.net_profit_minor ?? todayRow?.net_profit_minor ?? 0;
  const yesterdayNetProfit = yesterdayRow?.net_profit_minor ?? 0;
  const profitDelta = pctDelta(todayNetProfit, yesterdayNetProfit);

  return (
    <Stack gap="xl">
      <PageHeader
        title={`Hoş geldin, ${user?.username} 👋`}
        subtitle="Bugünün özeti — restoranınızda neler oluyor?"
        actions={
          <Badge color={wsStatus === "OPEN" ? "green" : "gray"} variant="dot" size="lg">
            {wsStatus === "OPEN" ? "Canlı" : "Bağlanıyor..."}
          </Badge>
        }
      />

      {/* ============ 4 KPI Card Row ============ */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <KPICard
          title="Bugün Ciro"
          value={
            dailyLoading ? <Loader size="sm" /> :
            <MoneyDisplay minor={daily?.total_revenue_minor ?? 0} />
          }
          {...(revenueDelta == null ? {} : { trend: { value: revenueDelta, label: "vs dün" } })}
          icon={<IconCurrencyLira size={24} />}
          color="green"
        />
        <KPICard
          title="Sipariş Sayısı"
          value={todayOrderCount}
          {...(ordersDelta == null ? {} : { trend: { value: ordersDelta, label: "vs dün" } })}
          icon={<IconShoppingCart size={24} />}
          color="blue"
        />
        <KPICard
          title="Net Kâr"
          value={<MoneyDisplay minor={daily?.net_profit_minor ?? 0} />}
          {...(profitDelta == null ? {} : { trend: { value: profitDelta, label: "vs dün" } })}
          icon={<IconCoins size={24} />}
          color={(daily?.net_profit_minor ?? 0) >= 0 ? "teal" : "red"}
        />
        <KPICard
          title="Açık Sipariş"
          value={daily?.open_orders_count ?? 0}
          icon={<IconClock size={24} />}
          color="orange"
        />
      </SimpleGrid>

      {/* ============ 6 Ana Grafik Widget ============ */}
      <Grid>
        {/* Widget 1: Son 7 gün ciro trendi */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              <Group gap="xs">
                <IconTrendingUp size={20} />
                Son 7 Gün Ciro
              </Group>
            </Title>
            {last7Data.length > 0 ? (
              <AreaChart
                h={250}
                data={last7Data}
                dataKey="day"
                series={[{ name: "Ciro", color: "green.6" }]}
                curveType="natural"
                tickLine="xy"
                gridAxis="xy"
                valueFormatter={(v) => formatTRY((v as number) * 100)}
              />
            ) : (
              <Text c="dimmed" ta="center" py="xl">Veri yükleniyor...</Text>
            )}
          </Paper>
        </Grid.Col>

        {/* Widget 2: Ödeme tipi dağılımı (Donut) */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              <Group gap="xs">
                <IconReceipt size={20} />
                Ödeme Tipi Dağılımı
              </Group>
            </Title>
            <DonutChart
              h={250}
              data={[
                { name: "Nakit", value: daily?.cash_total_minor ?? 0, color: "green.6" },
                { name: "Kart", value: daily?.card_total_minor ?? 0, color: "blue.6" },
                { name: "QR", value: daily?.qr_payment_total_minor ?? 0, color: "violet.6" },
                { name: "Diğer", value: daily?.other_payment_total_minor ?? 0, color: "gray.6" },
              ]}
              chartLabel={formatTRY(
                (daily?.cash_total_minor ?? 0) +
                (daily?.card_total_minor ?? 0) +
                (daily?.qr_payment_total_minor ?? 0),
              )}
              withLabelsLine
              withLabels
            />
          </Paper>
        </Grid.Col>

        {/* Widget 3: Top satılan ürünler (Horizontal Bar) */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              <Group gap="xs">
                <IconChefHat size={20} />
                En Çok Satılan Ürünler
              </Group>
            </Title>
            {sales?.rows && sales.rows.length > 0 ? (
              <BarChart
                h={250}
                data={sales.rows.slice(0, 10).map((r: any) => ({
                  name: r.product_name,
                  qty: r.quantity_sold,
                }))}
                dataKey="name"
                series={[{ name: "qty", color: "blue.6", label: "Adet" }]}
                orientation="vertical"
                yAxisProps={{ width: 100 }}
              />
            ) : (
              <Text c="dimmed" ta="center" py="xl">Henüz satış yok</Text>
            )}
          </Paper>
        </Grid.Col>

        {/* Widget 4: Sipariş kaynak dağılımı (Pie) */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              <Group gap="xs">
                <IconUsers size={20} />
                Sipariş Kaynak Dağılımı
              </Group>
            </Title>
            <PieChart
              h={250}
              data={[
                { name: "POS", value: daily?.pos_order_count ?? 0, color: "blue" },
                { name: "QR", value: daily?.qr_order_count ?? 0, color: "violet" },
                { name: "Delivery", value: daily?.delivery_order_count ?? 0, color: "orange" },
              ]}
              withLabelsLine
              labelsType="value"
              withLabels
            />
          </Paper>
        </Grid.Col>

        {/* Widget 5: Aylık karlılık trendi (Line) */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              <Group gap="xs">
                <IconTrendingUp size={20} />
                Aylık Karlılık Trendi
              </Group>
            </Title>
            <LineChart
              h={250}
              data={mockMonthlyProfitData()}
              dataKey="month"
              series={[
                { name: "Gelir", color: "green.6" },
                { name: "Gider", color: "red.6" },
                { name: "Net Kâr", color: "blue.6" },
              ]}
              valueFormatter={(v) => formatTRY(v as number)}
              curveType="natural"
            />
          </Paper>
        </Grid.Col>

        {/* Widget 6: Masa durum dağılımı (mini visualization) */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              <Group gap="xs">
                <IconBuilding size={20} />
                Masa Durumu (Şu An)
              </Group>
            </Title>
            <Stack gap="sm">
              {tableOverview && Object.entries(tableOverview).map(([state, count]) => (
                <Group key={state} justify="space-between">
                  <Badge size="lg" variant="light" color={STATE_COLOR[state] || "gray"}>
                    {STATE_LABEL[state] || state}
                  </Badge>
                  <Text fw={700} size="lg">{count as number}</Text>
                </Group>
              ))}
              {!tableOverview && <Text c="dimmed">Yükleniyor...</Text>}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* ============ 5 Küçük Bonus Widget ============ */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
        <Card padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed">Kritik Stok</Text>
            <IconAlertTriangle size={20} color="red" />
          </Group>
          <Title order={2} c="red">{alerts?.results?.length ?? alerts?.length ?? 0}</Title>
          <Text size="xs" c="dimmed" mt="xs">Eşik altı malzeme</Text>
        </Card>

        <Card padding="lg" radius="md" withBorder
          style={{ borderColor: (daily?.total_loss_minor ?? 0) > 0 ? "#fa5252" : undefined }}>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed">Bugün Zayiat</Text>
            <IconTrendingDown size={20} color="red" />
          </Group>
          <Title order={2} c={(daily?.total_loss_minor ?? 0) > 0 ? "red" : "dimmed"}>
            <MoneyDisplay minor={daily?.total_loss_minor ?? 0} />
          </Title>
          <Text size="xs" c="dimmed" mt="xs">
            Fire {daily?.waste_count ?? 0} + Düzeltme {daily?.adjustment_count ?? 0}
          </Text>
        </Card>

        <Card padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed">Bahşiş Toplamı</Text>
            <IconCoins size={20} color="orange" />
          </Group>
          <Title order={2}>
            <MoneyDisplay minor={daily?.tip_total_minor ?? 0} />
          </Title>
          <Text size="xs" c="dimmed" mt="xs">Bugün</Text>
        </Card>

        <Card padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed">Toplam İndirim</Text>
            <IconTrendingDown size={20} color="violet" />
          </Group>
          <Title order={2}>
            <MoneyDisplay minor={daily?.discount_total_minor ?? 0} />
          </Title>
          <Text size="xs" c="dimmed" mt="xs">Uygulanmış</Text>
        </Card>

        <Card padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed">Ortalama Sipariş</Text>
            <IconShoppingCart size={20} color="blue" />
          </Group>
          <Title order={2}>
            <MoneyDisplay minor={daily?.avg_order_value_minor ?? 0} />
          </Title>
          <Text size="xs" c="dimmed" mt="xs">Sipariş başı</Text>
        </Card>
      </SimpleGrid>

      {/* ============ Marj Uyarıları (BOM Cost → Margin) ============ */}
      {marginAlerts?.products && marginAlerts.products.length > 0 && (
        <Paper p="lg" radius="md" withBorder style={{ borderColor: "#fa5252" }}>
          <Group justify="space-between" mb="md">
            <Title order={4}>
              <Group gap="xs">
                <IconAlertTriangle size={20} color="#fa5252" />
                Düşük Marj / Zarar Eden Ürünler
              </Group>
            </Title>
            <Badge color="red" variant="filled" size="lg">
              {marginAlerts.total_alerts} ürün
            </Badge>
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            Reçete maliyeti ile satış fiyatı karşılaştırması (eşik: %{marginAlerts.threshold_percent} marj).
            Sahibi olarak fiyatları gözden geçirmeniz önerilir.
          </Text>
          <ScrollArea>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ürün</Table.Th>
                  <Table.Th>SKU</Table.Th>
                  <Table.Th ta="right">Satış</Table.Th>
                  <Table.Th ta="right">Maliyet</Table.Th>
                  <Table.Th ta="right">Marj</Table.Th>
                  <Table.Th ta="right">Önerilen Fiyat</Table.Th>
                  <Table.Th>Durum</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {marginAlerts.products.slice(0, 15).map((p: any) => (
                  <Table.Tr key={p.product_id}>
                    <Table.Td fw={500}>{p.product_name}</Table.Td>
                    <Table.Td><Badge variant="light">{p.product_sku}</Badge></Table.Td>
                    <Table.Td ta="right"><MoneyDisplay minor={p.unit_price_minor} /></Table.Td>
                    <Table.Td ta="right"><MoneyDisplay minor={p.unit_cost_minor} /></Table.Td>
                    <Table.Td ta="right" fw={700} c={p.margin_minor < 0 ? "red" : "orange"}>
                      {p.margin_percent.toFixed(1)}%
                    </Table.Td>
                    <Table.Td ta="right" c="green" fw={500}>
                      <MoneyDisplay minor={p.suggested_price_minor} />
                    </Table.Td>
                    <Table.Td>
                      <Badge color={p.severity === "CRITICAL" ? "red" : "orange"} variant="filled">
                        {p.severity === "CRITICAL" ? "ZARAR" : "DÜŞÜK"}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          {marginAlerts.total_alerts > 15 && (
            <Text size="xs" c="dimmed" mt="sm" ta="center">
              +{marginAlerts.total_alerts - 15} daha — Ürünler sayfasında tümünü görüntüleyin
            </Text>
          )}
        </Paper>
      )}

      {/* ============ Bonus: Top satışlar tablosu ============ */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">📊 En Çok Satılan Ürünler (Detay)</Title>
        {sales?.rows && sales.rows.length > 0 ? (
          <ScrollArea>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ürün</Table.Th>
                  <Table.Th>SKU</Table.Th>
                  <Table.Th ta="right">Adet</Table.Th>
                  <Table.Th ta="right">Ciro</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sales.rows.slice(0, 10).map((r: any) => (
                  <Table.Tr key={r.product_id}>
                    <Table.Td fw={500}>{r.product_name}</Table.Td>
                    <Table.Td><Badge variant="light">{r.product_sku}</Badge></Table.Td>
                    <Table.Td ta="right">{r.quantity_sold}</Table.Td>
                    <Table.Td ta="right" fw={700}>
                      <MoneyDisplay minor={r.revenue_minor} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        ) : (
          <Text c="dimmed" py="xl" ta="center">Henüz veri yok</Text>
        )}
      </Paper>
    </Stack>
  );
}

// ---------- Helpers ----------
const STATE_COLOR: Record<string, string> = {
  FREE: "green", RESERVED: "yellow", OCCUPIED: "red",
  CLEANING: "gray", OUT_OF_SERVICE: "dark",
};
const STATE_LABEL: Record<string, string> = {
  FREE: "🟢 Boş", RESERVED: "🟡 Rezerve", OCCUPIED: "🔴 Dolu",
  CLEANING: "⚪ Temizleniyor", OUT_OF_SERVICE: "⛔ Servis Dışı",
};

function mockMonthlyProfitData() {
  return Array.from({ length: 12 }, (_, i) => {
    const month = new Date(2024, i, 1).toLocaleDateString("tr", { month: "short" });
    const gelir = 50000 + Math.random() * 30000;
    const gider = 30000 + Math.random() * 20000;
    return { month, Gelir: gelir, Gider: gider, "Net Kâr": gelir - gider };
  });
}

// Stub: IconBuilding from tabler
function IconBuilding(props: any) {
  return <span {...props}>🏢</span>;
}
